import {
  RadixAlertDialog,
  RadixAlertDialogContent,
  RadixAlertDialogDescription,
  RadixAlertDialogFooter,
  RadixAlertDialogHeader,
  RadixAlertDialogTitle,
  RadixAlertDialogTrigger,
} from '../../../../components/ui/alert-dialog';
import { cn } from '../../../../lib/utils';
import { Button, ButtonState } from '../../../primitives/buttons';
import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

export interface AlertDialogProps {
  /** 다이얼로그가 열려있는지 여부 */
  open: boolean;
  /** 다이얼로그의 열림/닫힘 상태를 변경하는 함수 */
  setOpen: (open: boolean) => void;
  /** 다이얼로그의 헤더에 표시될 요소 */
  isShowHeaderNode?: boolean;
  /** 다이얼로그를 트리거하는 요소 */
  triggerNode?: React.ReactNode;
  /** 제목 위에 표시되는 요소 */
  titleTopNode?: React.ReactNode;
  /** 다이얼로그의 제목 요소 */
  titleNode?: React.ReactNode;
  /** 다이얼로그의 설명 요소 */
  descriptionNode?: React.ReactNode;
  /** 설명 아래에 표시되는 보조 텍스트 요소 */
  subTextNode?: React.ReactNode;
  /** 다이얼로그 본문에 표시될 콘텐츠 */
  children?: React.ReactNode;
  /** 푸터에 표시될 요소 */
  footerNode?: React.ReactNode;
  /** 기본 푸터 버튼(확인/취소)을 숨길지 여부 */
  isDefaultFooterButtonsHidden?: boolean;
  /** 트리거 요소에 적용할 클래스명 */
  triggerClassName?: string;
  /** 다이얼로그 콘텐츠에 적용할 클래스명 */
  contentClassName?: string;
  /** 푸터에 적용할 클래스명 */
  footerClassName?: string;
  /** 확인 버튼의 상태 */
  confirmButtonState?: ButtonState;
  /** 취소 버튼의 상태 */
  cancelButtonState?: ButtonState;
  /** 확인 버튼에 표시할 텍스트 */
  confirmText?: string;
  /** 취소 버튼에 표시할 텍스트 */
  cancelText?: string;
  /** 취소 버튼 클릭 시 실행할 함수 */
  onCancel?: () => void;
  /** 확인 버튼 클릭 시 실행할 함수 */
  onConfirm?: () => void;
  /** 취소 버튼 표시 여부 */
  isShowCancelButton?: boolean;
  /** 확인 버튼 표시 여부 */
  isShowConfirmButton?: boolean;
  /** 다이얼로그 콘텐츠에 적용할 클래스명 */
  dialogContentProps?: Omit<
    React.ComponentProps<typeof AlertDialogPrimitive.Content>,
    'className'
  >;
  /** 오버레이에 적용할 props */
  overlayProps?: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>;
}

export default function AlertDialog({
  open,
  setOpen,
  isShowHeaderNode = true,
  triggerNode,
  titleTopNode,
  titleNode,
  descriptionNode,
  subTextNode,
  children,
  footerNode,
  contentClassName,
  triggerClassName,
  footerClassName,
  onCancel,
  onConfirm,
  confirmText = '확인',
  cancelText = '취소',
  confirmButtonState = 'default',
  cancelButtonState = 'default',
  isDefaultFooterButtonsHidden = false,
  isShowCancelButton = true,
  isShowConfirmButton = true,
  dialogContentProps,
  overlayProps,
}: AlertDialogProps) {
  return (
    <RadixAlertDialog
      open={open}
      onOpenChange={setOpen}
    >
      {triggerNode && (
        <RadixAlertDialogTrigger
          className={cn(triggerClassName)}
          asChild
        >
          {triggerNode}
        </RadixAlertDialogTrigger>
      )}
      <RadixAlertDialogContent
        className={cn('max-w-[500px]', contentClassName)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayProps={overlayProps}
        {...dialogContentProps}
      >
        {isShowHeaderNode && (
          <RadixAlertDialogHeader>
            {titleTopNode && titleTopNode}
            <div className="flex flex-col gap-4xs">
              <RadixAlertDialogTitle>{titleNode}</RadixAlertDialogTitle>
              <RadixAlertDialogDescription className="text-body-medium text-sys-cont-neutral-default">
                {descriptionNode}
              </RadixAlertDialogDescription>
              <div className="text-body-small text-sys-cont-neutral-lightest">
                {subTextNode}
              </div>
            </div>
          </RadixAlertDialogHeader>
        )}
        {children}
        <RadixAlertDialogFooter className={cn(footerClassName)}>
          <div
            className={`flex w-full ${footerNode ? 'justify-between' : 'justify-end'}`}
          >
            {footerNode && footerNode}
            {!isDefaultFooterButtonsHidden && (
              <div className="flex gap-2xs">
                {isShowCancelButton && (
                  <Button
                    hierarchy="tertiary"
                    className="w-fit"
                    onClick={onCancel}
                    state={cancelButtonState}
                  >
                    {cancelText}
                  </Button>
                )}
                {isShowConfirmButton && (
                  <Button
                    hierarchy="primary"
                    className="w-fit"
                    onClick={onConfirm}
                    state={confirmButtonState}
                  >
                    {confirmText}
                  </Button>
                )}
              </div>
            )}
          </div>
        </RadixAlertDialogFooter>
      </RadixAlertDialogContent>
    </RadixAlertDialog>
  );
}
